import { useQuery } from "@tanstack/react-query";
import { get, patch, post, apiDelete } from "./divertidogsApiUtils";
import { MetodoPago } from "./ventasServicios";
import { AgendamientoResumido } from "./agendamientosServicios";
import { Pagina } from "./Pagina";

export interface AgendamientoGuarderiaResumido extends AgendamientoResumido {
  reservaGuarderia: ReservaGuarderia;
  reservaTransporteIda?: ReservaTransporte;
  reservaTransporteVuelta?: ReservaTransporte;
  reservaTransporteIdaVuelta?: ReservaTransporte;
}

export const isAgendamientoGuarderiaResumido = (
  agendamientoResumido: AgendamientoResumido
): agendamientoResumido is AgendamientoGuarderiaResumido => {
  return (
    agendamientoResumido.tipoServicio === "GUARDERIA" &&
    "reservaGuarderia" in agendamientoResumido
  );
};

export interface ReservaGuarderia {
  plan?: string;
  precioFinalPlan?: number;
  diasIndividuales?: number;
  precioFinalDiaIndividual?: number;
  servicioDiaIndividual?: string;
  total: number;
  fechasProgramadas: string[];
}

export interface ReservaTransporte {
  direccion: string;
  sector: string;
  tipoTrayecto: string;
  tarifa: string;
  precioFinalTarifa: number;
  total: number;
  fechasProgramadas: string[];
}

export type ReagendamientoRequest = {
  idReservaGuarderia: string;
  fechas: Record<string, string>;
};

export const reagendarGuarderia = async ({
  idAgendamiento,
  request,
}: {
  idAgendamiento: string;
  request: ReagendamientoRequest;
}) => {
  return patch(`agendamientos/guarderia/${idAgendamiento}/reagendar`, request);
};

export const QUERY_BUSCAR_AGENDAMIENTO_DE_RESERVA_GUARDERIA =
  "QUERY_BUSCAR_AGENDAMIENTO_DE_RESERVA_GUARDERIA";

export const useBuscarAgendamientoDeReservaGuarderia = (
  idReservaGuarderia: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: [
      QUERY_BUSCAR_AGENDAMIENTO_DE_RESERVA_GUARDERIA,
      idReservaGuarderia,
    ],
    queryFn: async () =>
      await buscarAgendamientoDeReservaGuarderia(idReservaGuarderia),
    enabled: enabled,
  });

const buscarAgendamientoDeReservaGuarderia = async (
  idReservaGuarderia: string
) => {
  let response = await get<AgendamientoGuarderiaResumido>(
    `agendamientos/guarderia/reserva/${idReservaGuarderia}`
  );
  return response;
};

export type AgendamientoListado = {
  id: string;
  nombrePropietario: string;
  nombreMascota: string;
  fechaRealizacion: string;
  nombreServicio: string;
  fueCancelado: boolean;
};

export const listarTodos = async (
  queryParamsStr: string
) => {
  let response = await get<Pagina<AgendamientoListado>>(
    `agendamientos/guarderia${queryParamsStr}`
  );
  return response;
};

export const QUERY_LISTAR_TODOS_AGENDAMIENTOS_GUARDERIA =
  "QUERY_LISTAR_TODOS_AGENDAMIENTOS_GUARDERIA";

export interface DatosTransporte {
  idSector: string;
  direccion: string;
  idTarifa: string;
  precioFinal: number;
  fechaInicio: string;
  fechaFin: string;
  fechasProgramadas: string[];
}

export interface PagoNuevoAgendamientoGuarderia {
  monto: number;
  metodo: MetodoPago;
}

export type NuevoAgendamientoGuarderia = NuevoAgendamientoGuarderiaRequest;

export interface NuevoAgendamientoGuarderiaRequest {
  idMascota: string;
  datosReservaGuarderia: {
    fechaInicio: string;
    fechaFin: string;
    fechasProgramadas: string[];
    idPlan?: string;
    precioFinalPlan?: number;
    diasIndividuales?: number;
    idServicioDiaIndividual?: string;
    precioFinalDiaIndividual?: number;
  };
  datosDiasIndividuales?: {
    diasIndividuales: number;
    precioFinal: string;
  };
  datosTransporteIda?: DatosTransporte;
  datosTransporteVuelta?: DatosTransporte;
  datosTransporteIdaVuelta?: DatosTransporte;
  pago?: PagoNuevoAgendamientoGuarderia;
  descuento: number;
}

export const agendar = async (
  nuevoAgendamientoGuarderia: NuevoAgendamientoGuarderia
) => {
  return post<NuevoAgendamientoGuarderiaRequest>(
    "agendamientos/guarderia",
    nuevoAgendamientoGuarderia
  );
};

export const eliminar = async (idAgendamiento: string) => {
  return apiDelete(`agendamientos/guarderia/${idAgendamiento}`);
};

export const buscarPorId = async (id: string) => {
  return get<AgendamientoGuarderiaResumido>(`agendamientos/guarderia/${id}`);
}

export const QUERY_BUSCAR_AGENDAMIENTO_GUARDERIA_POR_ID =
  "QUERY_BUSCAR_AGENDAMIENTO_GUARDERIA_POR_ID";

export const useBuscarPorId = (
  id: string
) =>
  useQuery({
    queryKey: [QUERY_BUSCAR_AGENDAMIENTO_GUARDERIA_POR_ID],
    queryFn: async () =>
      await buscarPorId(id)
  });